import React, { useState } from 'react'
import { parseMetadata } from '../utils'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import {
  selectMeetingTemplates,
  selectSelectedMeetingTemplate,
  selectSessionMeetingTemplate,
  setSelectedMeetingTemplate,
} from '../../../../../store/meetingSummary/meetingTemplates'
import { useUpdateRoomSessionMetadata } from '../hooks/api'
import { showToast } from '../../../../../store/meetingSummary/display'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getRoomTrackingDetails } from '../../tracking'
import { UpdateMeetingTemplateModal } from '../UpdateMeetingTemplateModal/UpdateMeetingTemplateModal'
import './DeprecatedMeetingTemplateInfo.scss'

type Props = {
  variant: 'preparation' | 'summary'
}

export const DeprecatedMeetingTemplateInfo: React.FC<Props> = ({ variant }) => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const roomSession = useAppSelector(selectRoomSession)
  const sessionMeetingTemplate = useAppSelector(selectSessionMeetingTemplate)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const meetingTemplates = useAppSelector(selectMeetingTemplates)

  const updateRoomSessionMetadata = useUpdateRoomSessionMetadata()

  const [dismissed, setDismissed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const hasPendingChange = selectedMeetingTemplate != null && selectedMeetingTemplate.id !== sessionMeetingTemplate?.id

  if (!sessionMeetingTemplate || !sessionMeetingTemplate.isDeprecated || !sessionMeetingTemplate.newMeetingTemplateId || dismissed || hasPendingChange) {
    return null
  }

  const newTemplate = meetingTemplates.find((template) => template.id === sessionMeetingTemplate.newMeetingTemplateId)
  if (!newTemplate) {
    return null
  }

  const _handleUpdateMeeting = async () => {
    if (!roomSession || loading) {
      return
    }

    setLoading(true)
    const currentMetadata = parseMetadata(roomSession.metadata)
    const { external } = currentMetadata
    await updateRoomSessionMetadata({ ...external, meetingTemplateId: newTemplate.id })
    dispatch(setSelectedMeetingTemplate(newTemplate.id))
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'The meeting type has been updated.' }))
    setShowModal(false)
    setDismissed(true)
  }

  const handleUpdateMeeting = tracked(_handleUpdateMeeting, {
    eventName: TrackEvent.ROOM_SESSION_MEETING_TYPE_CHANGED,
    eventDetails: {
      ...getRoomTrackingDetails(roomSession),
      newMeetingTemplateId: newTemplate.id,
      source: 'deprecated-meeting-template-info',
    },
  })

  return (
    <>
      <div className='w-100 position-absolute d-flex align-items-center justify-content-between deprecated-meeting-template-info-container'>
        <div className='d-flex gap-3 align-items-center overflow-hidden'>
          <i className='bi bi-info-circle deprecated-meeting-template-info__icon' />
          <span className='paragraph-medium'>
            An improved meeting type is available. Update this meeting to{' '}
            <span className='deprecated-meeting-template-info__new-template-link'>{newTemplate.name}</span>{' '}
            {variant === 'summary' ? 'for better summaries.' : 'for better prep and summaries.'}
          </span>
        </div>
        <button
          type='button'
          className='deprecated-meeting-template-info__action paragraph-medium'
          onClick={() => (variant === 'summary' ? handleUpdateMeeting() : setShowModal(true))}
        >
          Update meeting
        </button>
      </div>
      <UpdateMeetingTemplateModal show={showModal} onHide={() => setShowModal(false)} onConfirm={handleUpdateMeeting} isLoading={loading} />
    </>
  )
}
